// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.511Z
// Collection: userProfiles/$walletAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * User profile keyed by wallet address. Stores display-case username and last change timestamp. Enforces 30-day cooldown on username changes. Deletable by the owner or by the backend (PROJECT_VAULT_ADDRESS) as part of the DELETE /api/profile creator-profile teardown flow.
 */

export interface UserProfilesRequest {
  username: string;
  lastUsernameChangeAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UserProfilesResponse {
  username: string;
  lastUsernameChangeAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a UserProfiles operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUserProfiles(walletAddress: string, data: UserProfilesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userProfiles/${walletAddress}`, document: doc };
}

/**
 * User creates their own profile (document ID must equal their wallet). Caller must equal $walletAddress. Username length 4-15 chars; frontend must additionally enforce ^[a-zA-Z0-9_]{4,15}$ character whitelist. Frontend should atomically also write usernames/<lowercase(username)> to claim the name. lastUsernameChangeAt should be set to current Unix seconds (Time.Now). (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUserProfiles(walletAddress: string, data: UserProfilesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userProfiles/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UserProfiles: ${formatError(error)}`);
    return false;
  }
}

export type UserProfilesRequestUpdate = Partial<UserProfilesRequest>;

/**
 * Build a UserProfiles update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateUserProfiles(walletAddress: string, data: UserProfilesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userProfiles/${walletAddress}`, document: doc };
}

/**
 * Only the wallet owner can update their profile. Username change cooldown enforced: @newData.lastUsernameChangeAt - @data.lastUsernameChangeAt must be >= 2592000 seconds (30 days). Frontend must set @newData.lastUsernameChangeAt to the current Unix seconds when changing username. To change names: (1) delete old usernames/<oldLower>, (2) create new usernames/<newLower>, (3) update userProfiles/$walletAddress with new username + new lastUsernameChangeAt. Length (4-15 chars) and character validation re-applied. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateUserProfiles(walletAddress: string, data: UserProfilesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userProfiles/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating UserProfiles: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read any user's profile (public display username and last change time).
   (Get Single Item)
 */
export async function getUserProfiles(walletAddress: string): Promise<UserProfilesResponse | null> {
  try {
    const result = await get(`userProfiles/${walletAddress}`);
    return result as UserProfilesResponse | null;
  } catch (error) {
    console.error(`Error getting UserProfiles: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UserProfiles document. (
  Read Operation Details: Anyone can read any user's profile (public display username and last change time).
  )
 */
export function subscribeUserProfiles(callback: (data: UserProfilesResponse | null) => void, walletAddress: string): Promise<() => Promise<void>> {
  return subscribe(`userProfiles/${walletAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UserProfilesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UserProfiles at path '${`userProfiles/${walletAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UserProfiles items from collection userProfiles
 
  Read Operation Details: Anyone can read any user's profile (public display username and last change time).
  
 */
export async function getManyUserProfiles(filter: string = ""): Promise<UserProfilesResponse[]> {
  try {
    const results = await get("userProfiles", { prompt: filter });
    return Array.isArray(results) ? results as UserProfilesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserProfiles collection at path 'userProfiles': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserProfiles collection at userProfiles
 
  Read Operation Details: Anyone can read any user's profile (public display username and last change time).
  
 */
export function subscribeManyUserProfiles(callback: (data: UserProfilesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userProfiles", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserProfilesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserProfiles collection at path '${"userProfiles"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UserProfiles items from collection userProfiles
 
  Read Operation Details: Anyone can read any user's profile (public display username and last change time).
  
 */
export async function getAllUserProfiles(filter: string = ""): Promise<UserProfilesResponse[]> {
  try {
    const results = await get("userProfiles", { prompt: filter });
    return Array.isArray(results) ? results as UserProfilesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserProfiles collection at path 'userProfiles': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserProfiles collection at userProfiles
 
  Read Operation Details: Anyone can read any user's profile (public display username and last change time).
  
 */
export function subscribeAllUserProfiles(callback: (data: UserProfilesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userProfiles", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserProfilesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserProfiles collection at path '${"userProfiles"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count UserProfiles items in collection userProfiles.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUserProfiles(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("userProfiles", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting UserProfiles at path 'userProfiles': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on UserProfiles items in collection userProfiles.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUserProfiles(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("userProfiles", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating UserProfiles at path 'userProfiles': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Allowed for the owner ($walletAddress == @user.address) or the backend vault (PROJECT_VAULT_ADDRESS). Frontend should NOT call delete directly — profile deletion is performed by the backend DELETE /api/profile route (running under PROJECT_VAULT_ADDRESS auth) which atomically tears down userProfiles, userAvatars, usernames, subscription rows in both directions, and soft-deletes the creator's wallpapers. The owner branch is permitted defensively but is not the supported UX path. After deletion, UI displays '@deleted' wherever a wallpaper's uploadedBy no longer resolves to a userProfile.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUserProfiles(walletAddress: string): Promise<boolean> {
  try {
    const succeeded = await set(`userProfiles/${walletAddress}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting UserProfiles: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for UserProfiles for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUserProfiles(walletAddress: string): DocumentOperation {
  return { path: `userProfiles/${walletAddress}`, document: null };
}

